import {
  CircleIcon,
  CloudIcon,
  DefaultSiteTemplateIcon,
  PluginIcon,
  SingleUserIcon,
  UserIcon,
} from "allIcons";
import {
  ListCard,
  ListIconWrapper,
  LoadingListCards,
  MainButton,
  ModuleLimitDetail,
  SecondaryHeading,
} from "commonUI";
import { ModulePrefixes, OnBoardingSteps, PluginVersion } from "enums";
import { useDashboardSteps, useUpdateDashboardSteps } from "hooks/dashboard";
import { t } from "i18next";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const onboradingSteps = {
  [OnBoardingSteps.VERIFY_YOUR_USER_AND_COMPANY_DETAILS]: {
    title: "dashboard.onboarding.step.verifyYourUserAndCompany",
    icon: <SingleUserIcon />,
  },
  [OnBoardingSteps.EXPLORE_THE_TEMPLATE_LIBRARY]: {
    title: "dashboard.onboarding.step.exploreTheTemplateLibrary",
    icon: <CloudIcon />,
  },
  [OnBoardingSteps.ADD_TEAM_MEMBERS_OR_CLIENTS]: {
    title: "dashboard.onboarding.step.addTeamMembersOrClients",
    icon: <UserIcon />,
  },
  [OnBoardingSteps.SET_UP_YOUR_SITE_TEMPLATE]: {
    title: "dashboard.onboarding.step.setupYourSiteTemplate",
    icon: <DefaultSiteTemplateIcon />,
  },
  [OnBoardingSteps.INSTALL_THE_PLUGIN_ON_YOUR_WORDPRESS_WEBSITE]: {
    title: "dashboard.onboarding.step.installThePluginOnYourWordpressWebsite",
    icon: <PluginIcon />,
  },
};

export default function OnBoarding() {
  const { data, isLoading } = useDashboardSteps();
  const { mutate: updateDashboardStep } = useUpdateDashboardSteps();
  const navigate = useNavigate();

  const completedSteps = useMemo(() => {
    if (isLoading || !data) return 0;
    return Object.keys(data).filter((key) => data?.[key]).length;
  }, [data, isLoading]);

  const handleDownloadPlugin = () => {
    window.open(
      `https://api.wpsitelauncher.com/download/wp-sitelauncher-${PluginVersion}.zip`,
      "_blank"
    );
  };

  const onStartStep = (step) => {
    switch (step) {
      case OnBoardingSteps.VERIFY_YOUR_USER_AND_COMPANY_DETAILS:
        if (!data[step]) {
          updateDashboardStep({ name: step });
        }
        navigate("/users");
        break;
      case OnBoardingSteps.EXPLORE_THE_TEMPLATE_LIBRARY:
        if (!data[step]) {
          updateDashboardStep({ name: step });
        }
        navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}`);
        break;
      case OnBoardingSteps.ADD_TEAM_MEMBERS_OR_CLIENTS:
        navigate("/users/team-members/add");
        break;
      case OnBoardingSteps.SET_UP_YOUR_SITE_TEMPLATE:
        if (!data[step]) {
          updateDashboardStep({ name: step });
        }
        navigate(`/${ModulePrefixes.SITE_TEMPLATE}/`);
        break;
      case OnBoardingSteps.INSTALL_THE_PLUGIN_ON_YOUR_WORDPRESS_WEBSITE:
        handleDownloadPlugin();
        break;
      default:
        break;
    }
    // Handle the start step action here
  };

  return (
    <div className="bg-white gap-15 p-5 rounded-5">
      <div>
        <SecondaryHeading>{t("dashboard.onboarding.title")}</SecondaryHeading>
        <p className="body-text m-0">
          {t("dashboard.onboarding.explainerText")}
        </p>
      </div>
      <div className="mt-3">
        <ModuleLimitDetail
          title={t("dashboard.onboarding.progressText", {
            completedSteps: completedSteps,
            totalSteps: Object.keys(onboradingSteps).length,
          })}
          progress={
            (completedSteps / Object.keys(onboradingSteps).length) * 100
          }
          barHeight="10px"
          barStyle={{
            marginTop: "4px",
          }}
        />
      </div>
      <div className="mt-5">
        {isLoading ? (
          <LoadingListCards />
        ) : (
          Object.keys(onboradingSteps).map((key) => (
            <ListCard
              data={t(onboradingSteps[key].title)}
              icon={
                <ListIconWrapper>{onboradingSteps[key].icon}</ListIconWrapper>
              }
              titleClass={data?.[key] ? "opacity-50 strike-through" : ""}
              deleteIcon={true}
              editIcon={false}
              rightSideContent={
                data?.[key] ? (
                  <MainButton className={"bg-white hover:bg-white text-black"} onClick={() => onStartStep(key)}>
                    <CircleIcon fill="#34C759" />
                    {t("dashboard.onboarding.step.completed")}
                  </MainButton>
                ) : (
                  <MainButton onClick={() => onStartStep(key)}>
                    {t(
                      key ===
                        OnBoardingSteps.INSTALL_THE_PLUGIN_ON_YOUR_WORDPRESS_WEBSITE
                        ? "sites.apiKeyDetail.downloadPluginButton"
                        : "dashboard.onboarding.step.start"
                    )}
                  </MainButton>
                )
              }
            />
          ))
        )}
      </div>
    </div>
  );
}
